import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { map, Observable, shareReplay } from 'rxjs';
import { UploadDocument } from '../upload-document/upload-document';
import { DocxPreview } from '../document-editor/docx-preview';
import { DocumentTypeEnum } from '../../_enums/document-type.enum';
import { DocumentService } from '../../_services/document.service';
import { RerunTask } from '../rerun-task/rerun-task';
import { Waiting } from '../waiting/waiting';
import { buildDocumentFilename } from '../../_helpers/document-filename.util';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { TaskService } from '../../_services/task.service';
import { TemplateCatalogService } from '../../_services/template-catalog.service';
import { TemplateCategoryEnum } from '../../_enums/template-category.enum';
import { TemplateEntry } from '../../_models/template-entry.model';
import { InlineSnackbar } from '../inline-snackbar/inline-snackbar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SnackbarType } from '../../_enums/snackbar-type.enum';
import { isSameProduct, Suggestion } from '../../_models/product-suggestion-res.model';

@Component({
  selector: 'app-make-decision',
  imports: [
    TranslateModule,
    MatButton,
    UploadDocument,
    RerunTask,
    Waiting,
    MatTooltipModule,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    InlineSnackbar,
    MatCheckboxModule,
  ],
  templateUrl: './make-decision.html',
  styleUrl: './make-decision.scss',
})
export class MakeDecision implements OnInit {
  @Input() werkprocesNummer: number | null = null;
  @Input() clientNummer: number | null = null;
  // Selected in the product suggestion step.
  @Input() selectedProducts: Suggestion[] = [];
  @Output() selectedProductsChange = new EventEmitter<Suggestion[]>();

  public decisionError = false;
  public decisionFile: File | undefined;
  public actionFileExists = false;
  public isCreatingDecision = false;
  public DocumentTypeEnum = DocumentTypeEnum;
  public readonly SnackbarType = SnackbarType;
  // De beschikkingssjablonen van deze tenant; de gebruiker kiest er zelf een, net als
  // bij het actieplan.
  public templateTypes: TemplateEntry[] = [];
  public templatesError = false;
  public selectedTemplateType: TemplateEntry | null = null;
  // Selection on opening the step; unchecked products stay listed until the step is reopened.
  public listedProducts: Suggestion[] = [];

  private isDecisionUpToDate = false;
  private pendingDecision$: Observable<File> | null = null;
  private pendingTitle: string | undefined;

  get isCreateDecisionDisabled(): boolean {
    return (
      !this.selectedTemplateType ||
      !this.actionFileExists ||
      this.isCreatingDecision ||
      this.isDecisionUpToDate
    );
  }

  private readonly documentService = inject(DocumentService);
  private readonly taskService = inject(TaskService);
  private readonly docxPreview = inject(DocxPreview);
  private readonly templateCatalogService = inject(TemplateCatalogService);

  public ngOnInit(): void {
    this.listedProducts = [...this.selectedProducts];
    this.loadTemplates();
    this.subscribeToDocumentChanges();
  }

  public compareTemplateTypes(t1: TemplateEntry | null, t2: TemplateEntry | null): boolean {
    return t1?.task === t2?.task;
  }

  public isSelected(product: Suggestion): boolean {
    return this.selectedProducts.some((p) => isSameProduct(p, product));
  }

  public updateSelection(checked: boolean, product: Suggestion): void {
    this.selectedProducts = checked
      ? [...this.selectedProducts, product]
      : this.selectedProducts.filter((p) => !isSameProduct(p, product));
    this.selectedProductsChange.emit(this.selectedProducts);
  }

  // Een ander sjabloon maakt de bestaande beschikking verouderd: opnieuw maken mag weer.
  public onTemplateTypeChange(): void {
    this.isDecisionUpToDate = false;
  }

  public previewDecision(): void {
    if (this.pendingDecision$) {
      this.openPreview(this.pendingDecision$, this.pendingTitle);
      return;
    }

    if (this.decisionFile) {
      this.openPreview(this.decisionFile);
    }
  }

  public createDecision(): void {
    const template = this.selectedTemplateType?.task;
    if (!template) return;

    this.isCreatingDecision = true;
    this.decisionError = false;
    const optionalFiles = this.documentService.getOptionalDocumentsValue();
    // The template this decision is made from, even if the selection changes meanwhile.
    const title = this.selectedTemplateType?.label;
    const decision$ = this.taskService
      .createDecision(
        template,
        this.selectedProducts.map((p) => p.product),
        this.documentService.getDocumentSnapshot(DocumentTypeEnum.ACTION_PLAN) as Blob,
        optionalFiles,
        this.clientNummer,
        this.werkprocesNummer,
      )
      .pipe(
        map(
          (file) =>
            new File([file], buildDocumentFilename(title ?? 'beschikking', this.werkprocesNummer), {
              type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            }),
        ),
        shareReplay(1),
      );

    this.pendingDecision$ = decision$;
    this.pendingTitle = title;

    decision$.subscribe({
      next: (decision) => {
        this.pendingDecision$ = null;
        this.isCreatingDecision = false;
        this.isDecisionUpToDate = true;
        this.documentService.setDocument(DocumentTypeEnum.DECISION, decision, title);
      },
      error: () => {
        this.pendingDecision$ = null;
        this.isCreatingDecision = false;
        this.decisionError = true;
      },
    });

    this.openPreview(decision$, title);
  }

  private openPreview(
    file: File | Observable<File>,
    title = this.documentService.getDocumentTitle(DocumentTypeEnum.DECISION),
  ): void {
    this.docxPreview.open({
      file,
      // The template it was made from; a decision the user uploaded shows its file name.
      title,
      edited: (edited) => this.documentService.replaceDocument(DocumentTypeEnum.DECISION, edited),
    });
  }

  private loadTemplates(): void {
    this.templateCatalogService.byCategory(TemplateCategoryEnum.DECISION).subscribe({
      next: (templates) => {
        this.templateTypes = templates;
      },
      error: () => {
        this.templatesError = true;
      },
    });
  }

  private subscribeToDocumentChanges(): void {
    this.documentService.getDocument(DocumentTypeEnum.ACTION_PLAN).subscribe((file) => {
      this.actionFileExists = !!file;
    });

    this.documentService.getDocument(DocumentTypeEnum.DECISION).subscribe((file) => {
      this.decisionFile = file;
      this.isDecisionUpToDate = !!file;
    });

    this.documentService.isDocumentChanged(DocumentTypeEnum.ACTION_PLAN).subscribe((changed) => {
      if (changed) {
        this.documentService.setDocumentChanged(DocumentTypeEnum.ACTION_PLAN, false);
        this.isCreatingDecision = false;
        this.isDecisionUpToDate = false;
      }
    });

    this.documentService.isOptionalDocumentsChanged().subscribe((changed) => {
      if (changed) {
        this.isDecisionUpToDate = false;
      }
    });
  }
}
